import { updateData } from "../../Services/updateData";
import { VehicleModel } from "../../Utils/Models";
import { endpoints } from "../../Utils/UrlEnum";
import { genericEmptyField } from "../../Utils/Utils";

/**
 * Updates a vehicle with the given model and props.
 *
 * @param {any} model The model representing the vehicle to be updated.
 * @param {any} props The props containing the onClose and setContextValue functions.
 *
 * @returns {Promise<void>} An empty promise that resolves when the vehicle is updated.
 */
export const updateVehicle = async (model: any, props: any) => {
  const url = `${endpoints.vehicle.main}/${model?._id}`;
  const formData = new FormData();

  // Create an array for metadata, including file name and extension
  const documentsMeta = model.document_ids.map((doc: any) => {
    let documentName = "";
    let documentExtension = "";
    let _id: string | null = "";
    if (doc.document instanceof File) {
      _id = null;
      documentName = doc.document.name;
      const parts = doc.document.name.split(".");
      documentExtension = parts.length > 1 ? parts.pop() || "" : "";
    } else {
      _id = doc._id;
      documentName = doc.document; // Existing document URL/path
    }
    return {
      _id: _id,
      name: doc.name,
      expirationDate: doc.expirationDate,
      documentName,
      documentExtension,
    };
  });

  // Append metadata as a JSON string.
  formData.append("document_ids_meta", JSON.stringify(documentsMeta));

  // Append metadata as a JSON string.
  formData.append("revisionIds", JSON.stringify(model.revisionIds));

  formData.append(
    "workPointId",
    typeof model.workPointId === "string"
      ? model.workPointId // If it's a string, append it directly
      : model.workPointId?._id || "" // If it's an object, append _id, otherwise append an empty string
  );

  // Append each new file using the key "document_ids".
  model.document_ids.forEach((doc: any) => {
    if (doc.document instanceof File) {
      formData.append("document_ids", doc.document);
    }
  });

  // Append the rest of the model fields.
  Object.keys(model).forEach((key) => {
    if (!formData.has(key)) {
      formData.append(key, model[key]);
    }
  });

  try {
    const res = (await updateData(url, formData)) as any;
    if (!res || res?.error) {
      console.error("Error response:", res);
      return;
    }

    if (props) {
      props?.setContextValue([]);
      props?.onClose();
    }
  } catch (error) {
    console.error("Error updating vehicle:", error);
  }
};

/**
 * Returns a string label for a vehicle, composed of its brand, model and vehicle number.
 *
 * @param {VehicleModel} vehicle The vehicle to generate a label for.
 * @returns {string} A string label for the vehicle.
 */
export const getVehicleLabel = (
  vehicle: VehicleModel | void | string,
  vehicles: VehicleModel[] = []
) => {
  if (!vehicle) return genericEmptyField;
  if (typeof vehicle === "string") {
    vehicle = vehicles?.find((v: VehicleModel) => v?._id === vehicle);
  }
  return `${vehicle?.brand} ${vehicle?.model} ${vehicle?.vehicleNumber}`;
};
